import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { refreshBorrowerCreditEventsAction } from 'store/loanApplications/actions';

import { BorrowersTestIds } from '../../../../../../../Borrowers.testIds';

import {
  CREDIT_REPORT_EVENT_DATES_TITLE,
  REFRESH_BUTTON_LABEL,
  RELIABILITY_LABEL_VARIANT,
} from './CreditReportEventDates.constants';
import { dateRowStyles, panelHeaderStyles, panelStyles } from './CreditReportEventDates.styles';
import {
  getCreditReportDateRows,
  getCreditReportSourceText,
  getNoCreditReportDatesText,
  getRefreshResultMessage,
} from './CreditReportEventDates.utils';

/**
 * The borrower's bankruptcy, foreclosure, short-sale and deed-in-lieu dates read from the credit
 * report, read-only beside the loan officer's own dates, with a re-read of the stored report.
 */
const CreditReportEventDates = ({
  applicationId,
  borrowerId,
  finances = null,
  creditReportReferenceNumber = null,
  isDisabled = false,
  onRefreshed,
}) => {
  const dispatch = useDispatch();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);

  const rows = useMemo(() => getCreditReportDateRows(finances), [finances]);
  const sourceText = useMemo(
    () => getCreditReportSourceText({ finances, creditReportReferenceNumber }),
    [finances, creditReportReferenceNumber],
  );

  const onRefreshClick = useCallback(() => {
    setIsRefreshing(true);
    setRefreshMessage(null);

    dispatch(refreshBorrowerCreditEventsAction({ applicationId, borrowerId }))
      .then(result => {
        setRefreshMessage(getRefreshResultMessage({ result, borrowerId }));

        return onRefreshed();
      })
      .catch(Error)
      .finally(() => setIsRefreshing(false));
  }, [applicationId, borrowerId, dispatch, onRefreshed]);

  return (
    <Box data-testid={BorrowersTestIds.CREDIT_REPORT_EVENT_DATES_PANEL} sx={panelStyles}>
      <Box sx={panelHeaderStyles}>
        <Typography variant="medium14">{CREDIT_REPORT_EVENT_DATES_TITLE}</Typography>
        <Button
          data-testid={BorrowersTestIds.CREDIT_REPORT_EVENT_DATES_REFRESH_BUTTON}
          disabled={isDisabled || isRefreshing}
          size="small"
          variant="outlined"
          onClick={onRefreshClick}
        >
          {REFRESH_BUTTON_LABEL}
        </Button>
      </Box>

      {rows.map(({ key, label, date, detail, reliability }) => (
        <Box
          data-testid={BorrowersTestIds.getCreditReportEventDateRowId(key)}
          key={key}
          sx={dateRowStyles}
        >
          <Typography color="text.secondary" variant="regular14">
            {label}
          </Typography>
          <Typography variant="regular14">{detail ? `${date} · ${detail}` : date}</Typography>
          {reliability && (
            <LabelCDS
              dataTestId={BorrowersTestIds.getCreditReportEventReliabilityId(key)}
              size={LabelSize.S}
              title={reliability}
              variant={RELIABILITY_LABEL_VARIANT[reliability]}
            />
          )}
        </Box>
      ))}

      {!rows.length && (
        <Typography
          color="text.secondary"
          data-testid={BorrowersTestIds.CREDIT_REPORT_EVENT_DATES_EMPTY}
          variant="regular14"
        >
          {getNoCreditReportDatesText(finances)}
        </Typography>
      )}

      {sourceText && (
        <Typography
          color="text.secondary"
          data-testid={BorrowersTestIds.CREDIT_REPORT_EVENT_DATES_SOURCE}
          variant="regular12"
        >
          {sourceText}
        </Typography>
      )}

      {refreshMessage && (
        <Typography
          data-testid={BorrowersTestIds.CREDIT_REPORT_EVENT_DATES_REFRESH_RESULT}
          role="status"
          variant="regular12"
        >
          {refreshMessage}
        </Typography>
      )}
    </Box>
  );
};

CreditReportEventDates.propTypes = {
  applicationId: PropTypes.string.isRequired,
  borrowerId: PropTypes.string.isRequired,
  onRefreshed: PropTypes.func.isRequired,
  finances: PropTypes.object,
  creditReportReferenceNumber: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default memo(CreditReportEventDates);
